import { Head, router } from "@inertiajs/react"
import { Plus, Trash2 } from "lucide-react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import { DataTable, Td, Th, Thead, Tr } from "@/components/steel/table"
import { Field, Kpi, KpiStrip, PageHeader, Section } from "@/components/steel"
import { Input } from "@/components/ui/input"
import { formatCurrency } from "@/lib/utils"

type Option = { id: string; name: string }

type PayrollRecord = {
  burdenPct: number
  employeeId: string
  employeeName: string
  grossAmount: number
  id: number
  loadedCost: number
  paidHours: number
  periodEnd: string
  periodStart: string
  unallocated: number
}

type Props = {
  employees: Option[]
  records: PayrollRecord[]
  unallocatedTotal: number
}

const BLANK = { burdenPct: "", employeeId: "", grossAmount: "", paidHours: "", periodEnd: "", periodStart: "" }

function formatPeriod(start: string, end: string) {
  const options = { month: "short", day: "numeric" } as const
  const from = new Date(`${start}T00:00:00`).toLocaleDateString("en-US", options)
  const to = new Date(`${end}T00:00:00`).toLocaleDateString("en-US", options)
  return `${from} – ${to}`
}

export default function PayrollIndex({ employees, records, unallocatedTotal }: Props) {
  const [form, setForm] = useState(BLANK)

  const loadedTotal = records.reduce((sum, r) => sum + r.loadedCost, 0)
  const set = (key: keyof typeof BLANK, value: string) => setForm((f) => ({ ...f, [key]: value }))

  const submit = () => {
    router.post("/employee_payroll_records", form, {
      preserveScroll: true,
      onSuccess: () => setForm(BLANK),
    })
  }

  const remove = (id: number) => {
    router.delete(`/employee_payroll_records/${id}`, { preserveScroll: true })
  }

  return (
    <>
      <Head title="Payroll" />
      <PageHeader title="Payroll" meta={`${records.length} records`} />

      <KpiStrip>
        <Kpi accent label="Burdened payroll" value={formatCurrency(loadedTotal)} />
        <Kpi
          label="Unallocated labor"
          value={formatCurrency(unallocatedTotal)}
          hint="Pay not attributable to any project"
        />
      </KpiStrip>

      <Section className="pb-7">
        <div className="mb-5 grid grid-cols-[repeat(auto-fit,minmax(130px,1fr))] items-end gap-3">
          <Field label="Employee">
            <select
              className="h-8 rounded-sm border border-border bg-transparent px-2 text-body"
              onChange={(e) => set("employeeId", e.target.value)}
              value={form.employeeId}
            >
              <option value="">Select…</option>
              {employees.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Period start">
            <Input onChange={(e) => set("periodStart", e.target.value)} type="date" value={form.periodStart} />
          </Field>
          <Field label="Period end">
            <Input onChange={(e) => set("periodEnd", e.target.value)} type="date" value={form.periodEnd} />
          </Field>
          <Field label="Gross">
            <Input onChange={(e) => set("grossAmount", e.target.value)} type="number" value={form.grossAmount} />
          </Field>
          <Field label="Paid hours">
            <Input onChange={(e) => set("paidHours", e.target.value)} type="number" value={form.paidHours} />
          </Field>
          <Field label="Burden %">
            <Input onChange={(e) => set("burdenPct", e.target.value)} type="number" value={form.burdenPct} />
          </Field>
          <Button onClick={submit} size="sm">
            <Plus className="size-3.5" /> Record
          </Button>
        </div>

        <DataTable>
          <Thead>
            <Tr>
              <Th>Employee</Th>
              <Th>Period</Th>
              <Th align="right">Gross</Th>
              <Th align="right">Paid hrs</Th>
              <Th align="right">Burden</Th>
              <Th align="right">Loaded cost</Th>
              <Th align="right">Unallocated</Th>
              <Th />
            </Tr>
          </Thead>
          <tbody>
            {records.map((record) => (
              <Tr key={record.id}>
                <Td strong>{record.employeeName}</Td>
                <Td muted>{formatPeriod(record.periodStart, record.periodEnd)}</Td>
                <Td align="right" num>
                  {formatCurrency(record.grossAmount)}
                </Td>
                <Td align="right" num>
                  {record.paidHours}
                </Td>
                <Td align="right" num>
                  {record.burdenPct}%
                </Td>
                <Td accent align="right" num>
                  {formatCurrency(record.loadedCost)}
                </Td>
                <Td align="right" muted num>
                  {formatCurrency(record.unallocated)}
                </Td>
                <Td align="right">
                  <button
                    aria-label="Delete payroll record"
                    className="text-muted-foreground hover:text-foreground"
                    onClick={() => remove(record.id)}
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                </Td>
              </Tr>
            ))}
          </tbody>
        </DataTable>
      </Section>
    </>
  )
}
